import { useState, useEffect } from 'react';
import {
  Drawer,
  Stack,
  Text,
  Title,
  Badge,
  Group,
  Divider,
  Paper,
  Grid,
  Code,
  Image,
  Box,
  Loader,
  Center,
  Alert,
  Card,
  ScrollArea,
} from '@mantine/core';
import { AlertCircle, Camera, MapPin, Clock } from 'lucide-react';
import { Detection } from '../types';
import { storage } from '../config/firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { WirelessSignalsDisplay } from './WirelessSignalsDisplay';
import Map, { Marker } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || '';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:9000';

interface DetectionDetailDrawerProps {
  detection: Detection | null;
  opened: boolean;
  onClose: () => void;
}

export function DetectionDetailDrawer({ detection, opened, onClose }: DetectionDetailDrawerProps) {
  const [plateImageUrl, setPlateImageUrl] = useState<string | null>(null);
  const [faceImageUrls, setFaceImageUrls] = useState<Record<number, string>>({});
  const [loadingPlateImage, setLoadingPlateImage] = useState(false);
  const [loadingFaceImages, setLoadingFaceImages] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  useEffect(() => {
    if (!detection || !opened) {
      setPlateImageUrl(null);
      setFaceImageUrls({});
      setImageError(null);
      return;
    }

    if (detection.plate?.image_url) {
      loadPlateImage(detection.plate.image_url);
    }

    if (detection.faces && detection.faces.length > 0) {
      loadFaceImages(detection.faces);
    }
  }, [detection, opened]);

  const loadPlateImage = async (imageUrl: string) => {
    setLoadingPlateImage(true);
    setImageError(null);

    try {
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        setPlateImageUrl(imageUrl);
      } else if (imageUrl.startsWith('/api/')) {
        const apiUrl = `${API_BASE_URL}${imageUrl}`;
        setPlateImageUrl(apiUrl);
      } else {
        const imageRef = ref(storage, imageUrl);
        const url = await getDownloadURL(imageRef);
        setPlateImageUrl(url);
      }
    } catch {
      setImageError('Failed to load plate image');
    } finally {
      setLoadingPlateImage(false);
    }
  };

  const loadFaceImages = async (faces: Detection['faces']) => {
    setLoadingFaceImages(true);
    setImageError(null);

    try {
      const urls: Record<number, string> = {};

      for (const face of faces) {
        try {
          if (face.image_url.startsWith('http://') || face.image_url.startsWith('https://')) {
            urls[face.image_id] = face.image_url;
          } else if (face.image_url.startsWith('/api/')) {
            const apiUrl = `${API_BASE_URL}${face.image_url}`;
            urls[face.image_id] = apiUrl;
          } else {
            const imageRef = ref(storage, face.image_url);
            const url = await getDownloadURL(imageRef);
            urls[face.image_id] = url;
          }
        } catch {
          // Silently skip failed images
        }
      }

      setFaceImageUrls(urls);
    } catch {
      setImageError('Failed to load face images');
    } finally {
      setLoadingFaceImages(false);
    }
  };

  if (!detection) {
    return null;
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      title="Detection Details"
    >
      <ScrollArea h="calc(100vh - 80px)">
        <Stack gap="lg">
          <Paper p="md" withBorder>
            <Grid>
              <Grid.Col span={6}>
                <Group gap="xs">
                  <Clock size={16} />
                  <Text size="sm" fw={500}>Timestamp</Text>
                </Group>
                <Text size="sm" mt="xs">
                  {new Date(detection.timestamp).toLocaleString()}
                </Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Group gap="xs">
                  <MapPin size={16} />
                  <Text size="sm" fw={500}>Location</Text>
                </Group>
                <Text size="sm" mt="xs">
                  {detection.location
                    ? `${detection.location.lat.toFixed(6)}, ${detection.location.lon.toFixed(6)}`
                    : 'N/A'}
                </Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="sm" fw={500}>Facility</Text>
                <Code mt="xs">{detection.facility_id}</Code>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="sm" fw={500}>Sensor</Text>
                <Code mt="xs">{detection.sensor_id}</Code>
              </Grid.Col>
              <Grid.Col span={12}>
                <Text size="sm" fw={500}>Persona ID</Text>
                <Code mt="xs">{detection.persona_id}</Code>
              </Grid.Col>
            </Grid>
          </Paper>

          <Divider />

          {detection.plate && (
            <>
              <Stack gap="md">
                <Group gap="xs">
                  <Camera size={20} />
                  <Title order={4}>License Plate</Title>
                </Group>

                <Card withBorder padding="lg">
                  <Stack gap="md">
                    <Group justify="space-between">
                      <div>
                        <Text size="sm" c="dimmed">Plate Number</Text>
                        <Badge size="xl" color="green" mt="xs">
                          {detection.plate.gemini_text || detection.plate.text}
                        </Badge>
                      </div>
                      {(detection.plate.gemini_confidence ?? detection.plate.confidence) != null && (
                        <div>
                          <Text size="sm" c="dimmed">Confidence</Text>
                          <Badge size="lg" color="blue" mt="xs">
                            {((detection.plate.gemini_confidence ?? detection.plate.confidence)! * 100).toFixed(1)}%
                          </Badge>
                        </div>
                      )}
                    </Group>

                    {detection.plate.image_url && (
                      <Box>
                        <Text size="sm" fw={500} mb="xs">
                          Plate Image
                        </Text>
                        {loadingPlateImage ? (
                          <Center h={200}>
                            <Loader size="md" />
                          </Center>
                        ) : plateImageUrl ? (
                          <Image
                            src={plateImageUrl}
                            alt="License plate"
                            radius="md"
                            fit="contain"
                            h={300}
                            style={{ border: '1px solid #dee2e6' }}
                          />
                        ) : (
                          <Alert color="yellow" icon={<AlertCircle size={16} />}>
                            Image not available
                          </Alert>
                        )}
                      </Box>
                    )}
                  </Stack>
                </Card>
              </Stack>

              <Divider />
            </>
          )}

          {detection.faces && detection.faces.length > 0 && (
            <>
              <Stack gap="md">
                <Group gap="xs">
                  <Camera size={20} />
                  <Title order={4}>Face Detections ({detection.faces.length})</Title>
                </Group>

                {imageError && (
                  <Alert color="red" icon={<AlertCircle size={16} />}>
                    {imageError}
                  </Alert>
                )}

                <Stack gap="md">
                  {detection.faces.map((face, idx) => (
                    <Card key={face.image_id} withBorder padding="lg">
                      <Stack gap="md">
                        <Group justify="space-between">
                          <Text fw={500}>Face {idx + 1}</Text>
                          <Badge color="blue">
                            Confidence: {(face.confidence * 100).toFixed(1)}%
                          </Badge>
                        </Group>

                        {face.bbox && (
                          <Box>
                            <Text size="sm" c="dimmed" mb="xs">
                              Bounding Box
                            </Text>
                            <Code block>
                              {JSON.stringify(face.bbox, null, 2)}
                            </Code>
                          </Box>
                        )}

                        <Box>
                          <Text size="sm" fw={500} mb="xs">
                            Face Image
                          </Text>
                          {loadingFaceImages ? (
                            <Center h={200}>
                              <Loader size="md" />
                            </Center>
                          ) : faceImageUrls[face.image_id] ? (
                            <Image
                              src={faceImageUrls[face.image_id]}
                              alt={`Face ${idx + 1}`}
                              radius="md"
                              fit="contain"
                              h={300}
                              style={{ border: '1px solid #dee2e6' }}
                            />
                          ) : (
                            <Alert color="yellow" icon={<AlertCircle size={16} />}>
                              Image not available
                            </Alert>
                          )}
                        </Box>

                        {face.gemini_analysis && (
                          <Box>
                            <Text size="sm" fw={500} mb="xs">
                              AI Analysis
                            </Text>
                            <Paper p="sm" bg="gray.0" withBorder>
                              <Grid>
                                {face.gemini_analysis.estimated_age_range && (
                                  <Grid.Col span={6}>
                                    <Text size="xs" c="dimmed">Age</Text>
                                    <Badge size="sm" color="blue">{face.gemini_analysis.estimated_age_range}</Badge>
                                  </Grid.Col>
                                )}
                                {face.gemini_analysis.estimated_gender && (
                                  <Grid.Col span={6}>
                                    <Text size="xs" c="dimmed">Gender</Text>
                                    <Badge size="sm" color="grape">{face.gemini_analysis.estimated_gender}</Badge>
                                  </Grid.Col>
                                )}
                                {face.gemini_analysis.expression && (
                                  <Grid.Col span={6}>
                                    <Text size="xs" c="dimmed">Expression</Text>
                                    <Badge size="sm" color="teal">{face.gemini_analysis.expression}</Badge>
                                  </Grid.Col>
                                )}
                                {face.gemini_analysis.has_glasses && (
                                  <Grid.Col span={6}>
                                    <Text size="xs" c="dimmed">Glasses</Text>
                                    <Badge size="sm" color="orange">{face.gemini_analysis.glasses_type || 'Yes'}</Badge>
                                  </Grid.Col>
                                )}
                                {face.gemini_analysis.has_hat && (
                                  <Grid.Col span={6}>
                                    <Text size="xs" c="dimmed">Hat</Text>
                                    <Badge size="sm" color="pink">
                                      {face.gemini_analysis.hat_type}{face.gemini_analysis.hat_logo ? ` (${face.gemini_analysis.hat_logo})` : ''}
                                    </Badge>
                                  </Grid.Col>
                                )}
                                {face.gemini_analysis.background_scene && (
                                  <Grid.Col span={6}>
                                    <Text size="xs" c="dimmed">Scene</Text>
                                    <Badge size="sm" color="gray">{face.gemini_analysis.background_scene}</Badge>
                                  </Grid.Col>
                                )}
                                {face.gemini_analysis.image_quality && (
                                  <Grid.Col span={6}>
                                    <Text size="xs" c="dimmed">Image Quality</Text>
                                    <Badge size="sm" color={
                                      face.gemini_analysis.image_quality === 'excellent' ? 'green' :
                                      face.gemini_analysis.image_quality === 'good' ? 'blue' :
                                      face.gemini_analysis.image_quality === 'fair' ? 'yellow' : 'red'
                                    }>{face.gemini_analysis.image_quality}</Badge>
                                  </Grid.Col>
                                )}
                                {face.gemini_analysis.face_angle && (
                                  <Grid.Col span={6}>
                                    <Text size="xs" c="dimmed">Face Angle</Text>
                                    <Badge size="sm" color="indigo">{face.gemini_analysis.face_angle}</Badge>
                                  </Grid.Col>
                                )}
                              </Grid>
                            </Paper>
                          </Box>
                        )}
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              </Stack>

              <Divider />
            </>
          )}

          {detection.wireless_signals && detection.wireless_signals.length > 0 && (
            <WirelessSignalsDisplay signals={detection.wireless_signals} />
          )}

          {!detection.plate &&
           (!detection.faces || detection.faces.length === 0) &&
           (!detection.wireless_signals || detection.wireless_signals.length === 0) && (
            <Alert color="gray" icon={<AlertCircle size={16} />}>
              No detection data available for this observation.
            </Alert>
          )}

          {detection.location && detection.location.lat && detection.location.lon && MAPBOX_TOKEN && (
            <>
              <Divider />
              <Stack gap="md">
                <Group gap="xs">
                  <MapPin size={20} />
                  <Title order={4}>Detection Location</Title>
                </Group>
                <Paper withBorder style={{ overflow: 'hidden', borderRadius: 8 }}>
                  <div style={{ height: '300px', width: '100%' }}>
                    <Map
                      initialViewState={{
                        longitude: detection.location.lon,
                        latitude: detection.location.lat,
                        zoom: 15,
                      }}
                      mapboxAccessToken={MAPBOX_TOKEN}
                      style={{ width: '100%', height: '100%' }}
                      mapStyle="mapbox://styles/mapbox/streets-v12"
                    >
                      <Marker
                        longitude={detection.location.lon}
                        latitude={detection.location.lat}
                        anchor="center"
                      >
                        <div
                          style={{
                            backgroundColor: '#228be6',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            border: '3px solid white',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                          }}
                        />
                      </Marker>
                    </Map>
                  </div>
                </Paper>
                <Text size="xs" c="dimmed" ta="center">
                  {detection.location.lat.toFixed(6)}, {detection.location.lon.toFixed(6)}
                </Text>
              </Stack>
            </>
          )}
        </Stack>
      </ScrollArea>
    </Drawer>
  );
}
